import { artworkCmd } from '../../services/apiClient';
import type { MediaKind, Model } from './contracts';
import { startingVideoModel } from './videoModel';

/**
 * The model Generate opens on, decided ONCE before the list is shown — never one model and then
 * another a second later (Farah, 27 Oct: the panel showed FLUX.2 Klein, then swapped to Atlas's).
 *
 *   1. the model Atlas is SURE of for this file and kind (artwork.ai.learned), when it is listed;
 *   2. else the platform default (Flow's lib/defaultModels, one list everywhere since 23 Oct);
 *   3. else the list's first.
 *
 * Atlas's answer is kept in this browser per file and kind, so a file opened before starts on it
 * at once; the first time, the panel waits a moment for it and then goes on without it. A late or
 * missing answer never changes a model on screen — it is only kept for the next open.
 */

/** The platform's own starting image model, best first (Flow's DEFAULT_MODELS.image). Video: startingVideoModel. */
const PLATFORM_IMAGE = ['gpt-image-2.5'];

/** How long a first open waits for Atlas before starting on the platform default. */
export const LEARNED_WAIT_MS = 700;

export interface Learned { model: string | null; aspect: string | null }

const KEY = 'flobi.artwork.learned.v1';
const KEEP = 200;
const FRESH_MS = 5 * 60_000;
const scope = (documentId: string, kind: MediaKind) => `${documentId}:${kind}`;

type Kept = Record<string, Learned & { at: number }>;
function readKept(): Kept {
  try { return (JSON.parse(localStorage.getItem(KEY) || 'null') as Kept | null) ?? {}; } catch { return {}; }
}
function keep(at: string, learned: Learned) {
  try {
    const all = readKept();
    all[at] = { ...learned, at: Date.now() };
    const rows = Object.entries(all).sort((a, b) => b[1].at - a[1].at).slice(0, KEEP);
    localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(rows)));
  } catch { /* full or blocked: the next open asks again */ }
}

/** Atlas's last answer for this file and kind kept in this browser, or undefined when it was never answered here. */
export function keptLearned(documentId: string, kind: MediaKind): Learned | undefined {
  const row = readKept()[scope(documentId, kind)];
  return row ? { model: row.model, aspect: row.aspect } : undefined;
}

/** One ask per file and kind every few minutes, shared by every caller; a failed ask is asked again next time. */
const asks = new Map<string, { at: number; answer: Promise<Learned | null> }>();
export function askLearned(documentId: string, kind: MediaKind): Promise<Learned | null> {
  const at = scope(documentId, kind);
  const held = asks.get(at);
  if (held && Date.now() - held.at < FRESH_MS) return held.answer;
  const answer = artworkCmd<{ model: { value: string } | null; aspect: { value: string } | null; unanswered?: boolean } | null>('artwork.ai.learned', { documentId, kind })
    .then((raw) => {
      // Atlas too slow this time: not "nothing learned", so what was kept stays and the next open asks again.
      if (raw?.unanswered) throw Error('unanswered');
      const learned = { model: raw?.model?.value ?? null, aspect: raw?.aspect?.value ?? null };
      keep(at, learned);
      return learned;
    })
    .catch(() => {
      asks.delete(at);
      return null;
    });
  asks.set(at, { at: Date.now(), answer });
  return answer;
}

/** Atlas's answer as a first open may use it: the kept one at once, else the live one if it comes within LEARNED_WAIT_MS. */
export function learnedForStart(documentId: string, kind: MediaKind): Promise<Learned | null> {
  const live = askLearned(documentId, kind);
  const kept = keptLearned(documentId, kind);
  if (kept) return Promise.resolve(kept);
  return Promise.race([live, new Promise<null>((done) => setTimeout(() => done(null), LEARNED_WAIT_MS))]);
}

/** The model a list opens on (see the top of this file). */
export function startingModel(kind: MediaKind, rows: Model[], learned: Learned | null): Model | undefined {
  const atlas = learned?.model ? rows.find((row) => row.id === learned.model) : undefined;
  if (atlas) return atlas;
  if (kind === 'video') return startingVideoModel(rows);
  return PLATFORM_IMAGE.map((id) => rows.find((row) => row.id === id)).find(Boolean) ?? rows[0];
}
